import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get, update, query, orderByChild, equalTo } from 'firebase/database';
import { fetchContact } from '../contacts';
import './Activity.css';

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length-1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function normalizeTime(t) {
  if (!t) return t;
  if (/[aApP][mM]$/i.test(t.trim())) return t; // already 12-hour AM/PM
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = mStr ? mStr.padStart(2, '0') : '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

const CalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const MsgIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const ArrowIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

function Activity() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const db   = getDatabase();
  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      if (user) loadAll(user.uid);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async (userId) => {
    setLoading(true);
    try {
      const [bookSnap, rideSnap, reqSnap, rentSnap, carSnap, userSnap] = await Promise.all([
        // Only bookings/rentals where you're the driver or owner.
        get(query(ref(db, 'bookings'), orderByChild('providerUserID'), equalTo(userId))),
        get(ref(db, 'rideReserve')),
        get(ref(db, 'rideRequest')),
        get(query(ref(db, 'rentalBookings'), orderByChild('ownerUserID'), equalTo(userId))),
        get(ref(db, 'cars')),
        get(ref(db, 'users')),
      ]);

      /* User map: display names only — phones come from fetchContact below */
      const uMap = {};
      if (userSnap.exists()) userSnap.forEach(s => {
        const u = s.val();
        uMap[s.key] = { name: u.name || u.username || 'Someone', phone: '' };
      });

      const ridesMap = {};
      if (rideSnap.exists()) rideSnap.forEach(s => { ridesMap[s.key] = s.val(); });

      const reqMap = {};
      if (reqSnap.exists()) reqSnap.forEach(s => { reqMap[s.key] = s.val(); });

      const carMap = {};
      if (carSnap.exists()) carSnap.forEach(s => { carMap[s.key] = s.val(); });

      const list = [];
      // Hide activity where the ride date has passed by more than 24 hours
      const isStillActive = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr).getTime() + 24 * 60 * 60 * 1000 >= Date.now();
      };

      if (bookSnap.exists()) {
        bookSnap.forEach(child => {
          const b    = child.val();
          const user = uMap[b.bookerUserID] || { name: 'Someone', phone: '' };
          const otherUserID = b.bookerUserID;

          /* Someone booked YOUR ride */
          if (b.type !== 'request' && b.providerUserID === userId && b.bookerUserID !== userId) {
            const ride = ridesMap[b.rideID];
            // Skip if ride was deleted or date has passed +24h
            if (!ride || !ride.fromLocation) return;
            if (!isStillActive(ride.date)) return;
            const rideTs = new Date(ride.date || 0).getTime();
            list.push({
              id:    `bk-${child.key}`,
              kind:  'booking',
              label: 'booked your ride',
              who:   user.name,
              otherUserID,
              from:  ride.fromLocation,
              to:    ride.toLocation   || '',
              date:  ride.date         || '',
              time:  ride.time         || '',
              note:  ride.additionalInfo || '',
              sortTs: rideTs,
            });
          }

          /* Someone fulfilled YOUR request */
          if (b.type === 'request' && b.providerUserID === userId && b.bookerUserID !== userId) {
            const req = reqMap[b.rideID];
            // Skip if request was deleted or date has passed +24h
            if (!req || !req.fromLocation) return;
            if (!isStillActive(req.date)) return;
            const reqTs = new Date(req.date || 0).getTime();
            list.push({
              id:    `fl-${child.key}`,
              kind:  'fulfilled',
              label: 'fulfilled your request',
              who:   user.name,
              otherUserID,
              from:  req.fromLocation,
              to:    req.toLocation   || '',
              date:  req.date         || '',
              time:  req.time         || '',
              note:  req.additionalInfo || '',
              sortTs: reqTs,
            });
          }
        });
      }

      /* Someone rented YOUR car */
      if (rentSnap.exists()) {
        rentSnap.forEach(child => {
          const r   = child.val();
          if (r.status === 'cancelled') return;   // skip cancelled bookings
          const car = carMap[r.carID] || {};
          if (r.ownerUserID !== userId) return;
          const user = uMap[r.renterUserID] || { name: 'Someone', phone: '' };
          const carLabel = [car.year, car.make, car.model].filter(Boolean).join(' ') || 'your car';
          list.push({
            id:         `rt-${child.key}`,
            kind:       'rental',
            label:      'rented your car',
            who:        user.name,
            otherUserID: r.renterUserID,
            carLabel,
            from:       r.startDate || '',
            to:         r.endDate   || '',
            sortTs:     new Date(r.startDate || 0).getTime(),
            bookingKey: child.key,
            carID:      r.carID,
          });
        });
      }

      // A booking/rental exists with each of these people, so their
      // contact can be shared. One lookup per person.
      const phones = {};
      await Promise.all([...new Set(list.map(i => i.otherUserID))].map(async uid => {
        phones[uid] = (await fetchContact(uid)).number;
      }));
      list.forEach(i => { i.phone = phones[i.otherUserID] || ''; });

      list.sort((a, b) => b.sortTs - a.sortTs);
      setItems(list);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const cancelRental = async (item) => {
    if (!window.confirm(`Cancel ${item.who.split(' ')[0]}'s rental of ${item.carLabel}? The car will be listed as available again.`)) return;
    const db = getDatabase();
    try {
      await update(ref(db, `rentalBookings/${item.bookingKey}`), { status: 'cancelled' });
      await update(ref(db, `cars/${item.carID}`), { availability: 'Available' });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      alert('Failed to cancel: ' + e.message);
    }
  };

  const kindCfg = {
    booking:  { color: '#2563eb', bg: 'rgba(37,99,235,.1)',   badge: 'Booking'   },
    fulfilled:{ color: '#059669', bg: 'rgba(5,150,105,.1)',   badge: 'Fulfilled' },
    rental:   { color: '#7c3aed', bg: 'rgba(124,58,237,.1)',  badge: 'Rental'    },
  };

  return (
    <div className="activity-container">
      <div className="activity-header">
        <h1>Activity</h1>
        <p className="activity-sub-head">Actions people took on your rides, requests &amp; cars</p>
      </div>

      {loading ? (
        <div className="activity-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="activity-empty">
          <div className="activity-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
            </svg>
          </div>
          <p><strong>No active activity</strong></p>
          <p>When someone books your ride, fulfills your request, or rents your car it'll show up here. Items disappear 24 hours after the trip date.</p>
        </div>
      ) : (
        <div className="activity-list">
          {items.map(item => {
            const cfg = kindCfg[item.kind];
            return (
              <div key={item.id} className="act-card">

                {/* Top: avatar + name + badge */}
                <div className="act-card-top">
                  <div className="act-avatar" style={{ background: cfg.bg, color: cfg.color }}>
                    {getInitials(item.who)}
                  </div>
                  <div className="act-who-wrap">
                    <span className="act-who">{item.who}</span>
                    <span className="act-label">{item.label}</span>
                  </div>
                  <span className="act-badge" style={{ color: cfg.color, background: cfg.bg }}>
                    {cfg.badge}
                  </span>
                </div>

                {/* Route */}
                {item.from && item.to && (
                  <div className="act-route">
                    <span className="act-city">{item.from}</span>
                    <span className="act-arrow"><ArrowIcon /></span>
                    <span className="act-city">{item.to}</span>
                  </div>
                )}
                {item.carLabel && (
                  <div className="act-route">
                    <span className="act-city">{item.carLabel}</span>
                  </div>
                )}

                {/* Date + time or rental dates */}
                <div className="act-meta">
                  {item.date ? (
                    <>
                      <span className="act-meta-item"><CalIcon />{fmtDate(item.date)}</span>
                      {item.time && <span className="act-meta-item"><ClockIcon />{normalizeTime(item.time)}</span>}
                    </>
                  ) : item.from && item.kind === 'rental' ? (
                    <span className="act-meta-item"><CalIcon />{item.from}{item.to ? ` → ${item.to}` : ''}</span>
                  ) : null}
                </div>

                {/* Note */}
                {item.note && <p className="act-note">"{item.note}"</p>}

                {/* Contact */}
                {item.phone ? (
                  <a className="act-contact-btn" href={`sms:${item.phone}`}>
                    <MsgIcon /> Text {item.who.split(' ')[0]} · {item.phone}
                  </a>
                ) : (
                  <span className="act-no-contact">No contact info on file</span>
                )}

                {/* Cancel rental */}
                {item.kind === 'rental' && (
                  <button className="act-cancel-btn" onClick={() => cancelRental(item)}>
                    Cancel Rental
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Activity;
